import { create } from 'zustand';
import {
    DASHBOARD_BREADCRUMB_TITLE,
    getBreadcrumbTitleForUrl,
    normalizeInAppUrl,
} from '@/lib/breadcrumbs/routeMetadata';

export interface BreadcrumbEntry {
    url: string;
    title: string;
}

export type BreadcrumbNavigationSource = 'sidebar' | 'panel' | 'browser_history' | 'terminal';

export interface BreadcrumbNavigationTarget {
    url: string;
    title?: string;
}

export interface BreadcrumbTrailState {
    items: BreadcrumbEntry[];
    activeUrl: string;
}

interface BreadcrumbStore extends BreadcrumbTrailState {
    pendingNavigationUrl: string | null;
    reset: () => void;
    setInitialTrail: (target: BreadcrumbNavigationTarget) => void;
    applyTrackedNavigation: (
        source: BreadcrumbNavigationSource,
        target: BreadcrumbNavigationTarget
    ) => void;
    applyNavigation: (
        source: BreadcrumbNavigationSource,
        target: BreadcrumbNavigationTarget
    ) => void;
    consumePendingNavigation: (url: string) => boolean;
}

export const DASHBOARD_BREADCRUMB_ENTRY: BreadcrumbEntry = {
    url: '/dashboard',
    title: DASHBOARD_BREADCRUMB_TITLE,
};

function createBreadcrumbEntry(target: BreadcrumbNavigationTarget): BreadcrumbEntry {
    const normalizedUrl: string | null = normalizeInAppUrl(target.url);
    if (normalizedUrl === null) {
        throw new Error(`Breadcrumb target must be an in-app URL: ${target.url}`);
    }

    const title: string | undefined = target.title?.trim();
    if (title !== undefined && title.length > 0) {
        return {
            url: normalizedUrl,
            title,
        };
    }

    const inferredTitle: string | null = getBreadcrumbTitleForUrl(normalizedUrl);
    if (inferredTitle === null) {
        throw new Error(`No breadcrumb title metadata found for URL: ${normalizedUrl}`);
    }

    return {
        url: normalizedUrl,
        title: inferredTitle,
    };
}

function ensureDashboardRoot(items: readonly BreadcrumbEntry[]): BreadcrumbEntry[] {
    if (items.length === 0) {
        return [DASHBOARD_BREADCRUMB_ENTRY];
    }

    if (items[0]?.url === DASHBOARD_BREADCRUMB_ENTRY.url) {
        return [...items];
    }

    return [DASHBOARD_BREADCRUMB_ENTRY, ...items];
}

function replaceOrAppend(
    items: readonly BreadcrumbEntry[],
    entry: BreadcrumbEntry
): BreadcrumbEntry[] {
    const existingIndex: number = items.findIndex((item) => item.url === entry.url);
    if (existingIndex < 0) {
        return [...items, entry];
    }

    return items.map((item, index) => (index === existingIndex ? entry : item));
}

function getActiveIndex(state: BreadcrumbTrailState): number {
    const activeIndex: number = state.items.findIndex((item) => item.url === state.activeUrl);
    if (activeIndex >= 0) {
        return activeIndex;
    }

    return Math.max(0, state.items.length - 1);
}

export function buildInitialBreadcrumbTrail(
    target: BreadcrumbNavigationTarget
): BreadcrumbTrailState {
    const entry: BreadcrumbEntry = createBreadcrumbEntry(target);

    if (entry.url === DASHBOARD_BREADCRUMB_ENTRY.url) {
        return {
            items: [DASHBOARD_BREADCRUMB_ENTRY],
            activeUrl: DASHBOARD_BREADCRUMB_ENTRY.url,
        };
    }

    return {
        items: [DASHBOARD_BREADCRUMB_ENTRY, entry],
        activeUrl: entry.url,
    };
}

export function applyBreadcrumbNavigation(
    state: BreadcrumbTrailState,
    source: BreadcrumbNavigationSource,
    target: BreadcrumbNavigationTarget
): BreadcrumbTrailState {
    const nextEntry: BreadcrumbEntry = createBreadcrumbEntry(target);
    const currentItems: BreadcrumbEntry[] = ensureDashboardRoot(state.items);
    const existingIndex: number = currentItems.findIndex((item) => item.url === nextEntry.url);

    if (existingIndex >= 0) {
        return {
            items: replaceOrAppend(currentItems, nextEntry),
            activeUrl: nextEntry.url,
        };
    }

    if (nextEntry.url === DASHBOARD_BREADCRUMB_ENTRY.url) {
        return {
            items: [DASHBOARD_BREADCRUMB_ENTRY],
            activeUrl: DASHBOARD_BREADCRUMB_ENTRY.url,
        };
    }

    if (source === 'sidebar') {
        return {
            items: [DASHBOARD_BREADCRUMB_ENTRY, nextEntry],
            activeUrl: nextEntry.url,
        };
    }

    const activeIndex: number = getActiveIndex({
        items: currentItems,
        activeUrl: state.activeUrl,
    });

    if (source === 'terminal') {
        const baseItems: BreadcrumbEntry[] = currentItems.slice(0, activeIndex);
        const itemsWithoutActive: BreadcrumbEntry[] = ensureDashboardRoot(baseItems);
        return {
            items: [...itemsWithoutActive, nextEntry],
            activeUrl: nextEntry.url,
        };
    }

    const baseItems: BreadcrumbEntry[] = currentItems.slice(0, activeIndex + 1);
    return {
        items: [...baseItems, nextEntry],
        activeUrl: nextEntry.url,
    };
}

const DEFAULT_BREADCRUMB_TRAIL_STATE: BreadcrumbTrailState = {
    items: [DASHBOARD_BREADCRUMB_ENTRY],
    activeUrl: DASHBOARD_BREADCRUMB_ENTRY.url,
};

export const useBreadcrumbStore = create<BreadcrumbStore>((set) => ({
    ...DEFAULT_BREADCRUMB_TRAIL_STATE,
    pendingNavigationUrl: null,
    reset: () => set({ ...DEFAULT_BREADCRUMB_TRAIL_STATE, pendingNavigationUrl: null }),
    setInitialTrail: (target) => set(buildInitialBreadcrumbTrail(target)),
    applyTrackedNavigation: (source, target) => {
        const normalizedUrl: string | null = normalizeInAppUrl(target.url);
        if (normalizedUrl === null) {
            throw new Error(`Breadcrumb target must be an in-app URL: ${target.url}`);
        }

        set((state) => ({
            ...applyBreadcrumbNavigation(state, source, target),
            pendingNavigationUrl: normalizedUrl,
        }));
    },
    applyNavigation: (source, target) =>
        set((state) => applyBreadcrumbNavigation(state, source, target)),
    consumePendingNavigation: (url) => {
        const normalizedUrl: string | null = normalizeInAppUrl(url);
        if (normalizedUrl === null) {
            return false;
        }

        let consumed = false;
        set((state) => {
            if (state.pendingNavigationUrl !== normalizedUrl) {
                return state;
            }

            consumed = true;
            return {
                ...state,
                pendingNavigationUrl: null,
            };
        });
        return consumed;
    },
}));
